import queryString from 'query-string';

const DEBUG_ENABLED_FLAG_QUERY_STRING_KEY = 'debug';

/**
 * Returns the provided queryString contains 'debug' flag
 *
 * Note: the 'debug' key does not require a value, just the presence
 * of it alone on the query string will return true
 */
export function queryStringContainsDebugFlag(qs) {
  if (!qs) return false;

  const queryStringKeyValueMap = queryString.parse(qs);
  const queryStringKeys = Object.keys(queryStringKeyValueMap);

  if (!queryStringKeys) return false;
  return queryStringKeys.includes(DEBUG_ENABLED_FLAG_QUERY_STRING_KEY);
}

/**
 * Transforms a featureControl object (built by GraphQL) into an
 * array of feature objects. The featureControl parameter must be in the schema of
 * {
 *   feature_name1: true/false/null,
 *   ...
 *   feature_nameN: true/false/null
 * }
 *
 * example:
 * featureControl = {
 *  addressSelector: true,
 *  displayBusinessDays: false,
 *  skipVatStep: null
 * }
 *
 * returns [  {featureName: 'addressSelector', enabled: true},
 *            {featureName: 'displayBusinessDays', enabled: false} ]
 *
 * Note 1: The value of a given feature_name will be null if
 * graphQL does not resolve a value for it. These features will not be included
 * in the responses object.
 *
 * Note 2: This only support binary toggling of a feature(enabled/disabled). In
 * the future we will want to extend our model to include multivariate features that
 * can supply additional feature settings.
 */
export function buildFeaturesFromSiteFeatureControls(featureControls = {}) {
  const allSiteConfiguredFeatureNames = Object.keys(featureControls);
  const activeFeatureNames = allSiteConfiguredFeatureNames.filter(
    name => featureControls[name] !== null
  );
  return activeFeatureNames.map(activeFeatureName => ({
    featureName: activeFeatureName,
    enabled: Boolean(featureControls[activeFeatureName]),
  }));
}

/**
 * Returns a final set of features that a given user should be exposed too.
 *
 * Any feature included in the debugFeatureOverrides parameter takes priority over the
 * same feature's settings as defined by site configs.
 *
 * Naieve approach for now - filter out all siteConfigurationFeatures that are present in the override array,
 * then spread the non-overrided features together with the overrided features from the debugFeatureOverrides parameter.
 *
 * @param {*} siteConfigurationFeatures an array of features defined by site configs
 * @param {*} debugFeatureOverrides an array of features which can override any given site config feature, or define new features.
 */
export function buildActiveFeatureSet({
  siteConfigurationFeatures = [],
  debugFeatureOverrides = [],
}) {
  const siteFeaturesWithoutOverrides = siteConfigurationFeatures.filter(
    siteFeature =>
      !debugFeatureOverrides.some(
        featureOverride =>
          featureOverride.featureName === siteFeature.featureName
      )
  );
  return [...siteFeaturesWithoutOverrides, ...debugFeatureOverrides];
}
